import React from 'react';
import {
  Autocomplete, Box, Divider, MenuItem, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { clientFields, FIELD_GROUPS } from '../../views/Customer/client.constants';

// field พิเศษที่ผูกกับ thai-data cascade (ดู utils/functions/thaiZipCascade.js): zipcode ขับเคลื่อน province/amphoe/tambon
function renderCascadeField(field, f, cascade, setSelections) {
  if (field.name === 'zipcode') {
    return (
      <TextField
        {...f}
        fullWidth
        label={field.label}
        size="small"
        inputProps={{ maxLength: 5, inputMode: 'numeric' }}
        error={cascade.status === 'not_found'}
        helperText={cascade.status === 'not_found' ? 'ไม่พบรหัสไปรษณีย์นี้ในระบบ' : undefined}
        onChange={(e) => {
          const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
          f.onChange(val);
          setSelections({ province: null, district: null });
        }}
      />
    );
  }

  const dropdownMap = {
    province: { options: cascade.provinceOptions, onPick: (val) => setSelections({ province: val, district: null }) },
    amphoe: { options: cascade.districtOptions, onPick: (val) => setSelections((prev) => ({ ...prev, district: val })) },
    tambon: { options: cascade.subdistrictOptions, onPick: () => {} },
  };
  const dropdown = dropdownMap[field.name];

  if (dropdown && dropdown.options.length > 0) {
    return (
      <TextField
        {...f}
        select
        fullWidth
        label={field.label}
        size="small"
        onChange={(e) => { f.onChange(e.target.value); dropdown.onPick(e.target.value); }}
      >
        {dropdown.options.map((name) => (
          <MenuItem key={name} value={name}>{name}</MenuItem>
        ))}
      </TextField>
    );
  }

  return <TextField {...f} fullWidth label={field.label} size="small" />;
}

function renderFieldControl(field, f, cascade, setSelections) {
  if (['zipcode', 'province', 'amphoe', 'tambon'].includes(field.name)) {
    return renderCascadeField(field, f, cascade, setSelections);
  }
  if (field.type === 'autocomplete') {
    return (
      <Autocomplete
        fullWidth
        size="small"
        options={field.options || []}
        value={f.value || null}
        onChange={(_, val) => f.onChange(val || '')}
        renderInput={(params) => <TextField {...params} label={field.label} />}
      />
    );
  }
  return (
    <TextField
      {...f}
      fullWidth
      select={field.type === 'select'}
      label={field.label}
      type={field.type === 'select' ? undefined : (field.type || 'text')}
      size="small"
      InputLabelProps={field.type === 'number' ? { shrink: true } : undefined}
    >
      {field.type === 'select' && field.options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

// grid ต่อเนื่องเดียวทั้งฟอร์ม (ไม่แยก sub-grid ต่อหมวด) เพื่อให้ความสูงรวมกระชับที่สุด ไม่ต้อง scroll ใน modal
// header/divider ของแต่ละหมวด sets gridColumn: '1 / -1' ให้กินเต็มความกว้างแล้วขึ้นแถวใหม่เอง
export default function ContactFormFields({ control, cascade, setZipSelections }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, columnGap: 2.5, rowGap: 1.25, alignItems: 'start' }}>
      {FIELD_GROUPS.map((group, groupIdx) => {
        const groupColor = theme.palette[group.colorKey].main;
        return (
          <React.Fragment key={group.key}>
            {groupIdx > 0 && (
              <Divider sx={{ gridColumn: '1 / -1', my: 0.5, borderColor: alpha(theme.palette.divider, 0.08) }} />
            )}
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, mt: groupIdx === 0 ? 0 : 0.5 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '7px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(groupColor, 0.12),
                  color: groupColor,
                  flexShrink: 0,
                }}
              >
                <group.icon sx={{ fontSize: 14 }} />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: groupColor, textTransform: 'uppercase' }}>
                {group.label}
              </Typography>
            </Box>
            {group.fieldNames.map((name) => {
              const field = clientFields.find((f) => f.name === name);
              return (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  render={({ field: f }) => renderFieldControl(field, f, cascade, setZipSelections)}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
